import * as THREE from 'three';

/**
 * What the viewer should tell `camera-controls` to do for a "look at this from
 * here" request (spec §11.3's `lookAt` event).
 *
 * This lives outside `Viewer.tsx` because `Viewer` cannot be mounted in jsdom
 * (WebGL), and the branch it encodes is the one that was missing: the `lookAt`
 * listener drove `setLookAt` in every camera mode, while its sibling
 * `jumpToTarget` has always branched on first person. In first person the orbit
 * radius is pinned at ~0 (`dollyTo(0.00001)`, `maxDistance={… : 0.00001}`), so
 * a left-drag means "look around in place" — a `setLookAt` that asks for a
 * camera several metres from its target either turns that into an orbit around
 * the section or collapses the pose, depending on when camera-controls next
 * re-clamps the radius. Pressing `L` while walking is exactly the case §11.3
 * exists for (finding I8).
 */
export interface LookAtPose {
  position: [number, number, number];
  target: [number, number, number];
}

/** The viewer's two camera-mode flags, as `Viewer` holds them. */
export interface LookAtCameraMode {
  /** Walk / fly mode. */
  flying: boolean;
  /** Third-person orbit inside walk mode; first person when false. */
  thirdperson: boolean;
}

/**
 * `origin` is what the floating origin is recentred on (`setPosition`). It
 * follows `jumpToTarget`: the camera in first person — where the camera IS the
 * user — and the target otherwise, which is what the orbit is about.
 */
export type LookAtCameraCommand =
  | {
      kind: 'orbit';
      position: [number, number, number];
      target: [number, number, number];
      origin: [number, number, number];
    }
  | {
      kind: 'firstPerson';
      position: [number, number, number];
      theta: number;
      phi: number;
      origin: [number, number, number];
    };

/**
 * Which camera-controls call the pose becomes, and with which arguments.
 *
 * First person gets `moveTo` + `rotateTo`, exactly as `jumpToTarget` does:
 * camera-controls works in a Y-up frame while the scene is Z-up, so the look
 * direction is negated (it expects target → camera) and rotated by −90° about
 * X before being read as spherical angles. Every other mode gets the plain
 * `setLookAt`, which is what the pose already describes.
 */
export function lookAtCameraCommand(pose: LookAtPose, mode: LookAtCameraMode): LookAtCameraCommand {
  const position: [number, number, number] = [...pose.position];
  const target: [number, number, number] = [...pose.target];
  if (!mode.flying || mode.thirdperson) {
    return { kind: 'orbit', position, target, origin: target };
  }
  const lookDir = new THREE.Vector3(...target).sub(new THREE.Vector3(...position)).normalize();
  const camCtrlDir = lookDir.negate().applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
  const spherical = new THREE.Spherical().setFromVector3(camCtrlDir);
  return { kind: 'firstPerson', position, theta: spherical.theta, phi: spherical.phi, origin: position };
}
